import React, { useMemo } from "react";
import { Clock3, Layers3, Link2, Package } from "lucide-react";

const getCampaignCount = (product) => {
    const count = Number(product?.dependent_sequence_count);

    return Number.isFinite(count) && count > 0 ? count : 0;
};

const getProductTime = (product) => {
    const value = product?.updated_at || product?.created_at;
    const time = value ? new Date(value).getTime() : 0;

    return Number.isNaN(time) ? 0 : time;
};

const ProductStatsCards = ({ products, isError = false }) => {
    const stats = useMemo(() => {
        if (isError) {
            return [
                {
                    label: "Total Products",
                    value: "--",
                    icon: Package,
                },
                {
                    label: "Total Products in Campaigns",
                    value: "--",
                    icon: Link2,
                },
                {
                    label: "Total Linked Campaigns",
                    value: "--",
                    icon: Layers3,
                },
                {
                    label: "Recently Updated Products",
                    value: "--",
                    icon: Clock3,
                    compactValue: true,
                },
            ];
        }

        const safeProducts = Array.isArray(products) ? products : [];
        const productsUsed = safeProducts.filter(
            (product) => getCampaignCount(product) > 0,
        ).length;
        const linkedCampaigns = safeProducts.reduce(
            (total, product) => total + getCampaignCount(product),
            0,
        );
        const latestProduct = [...safeProducts].sort(
            (first, second) =>
                getProductTime(second) - getProductTime(first),
        )[0];

        return [
            {
                label: "Total Products",
                value: safeProducts.length,
                icon: Package,
            },
            {
                label: "Total Products in Campaigns",
                value: productsUsed,
                icon: Link2,
            },
            {
                label: "Total Linked Campaigns",
                value: linkedCampaigns,
                icon: Layers3,
            },
            {
                label: "Recently Updated Products",
                value: latestProduct?.name || "No products yet",
                icon: Clock3,
                compactValue: true,
            },
        ];
    }, [isError, products]);

    return (
        <section
            aria-label="Product summary"
            className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        >
            {stats.map(({ label, value, icon: Icon, compactValue }) => (
                <article
                    key={label}
                    className="min-w-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4"
                >
                    <div className="flex items-center gap-2.5 sm:gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                            <Icon size={20} aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-500 sm:text-sm">
                                {label}
                            </p>
                            <p
                                className={`mt-1 truncate font-bold text-slate-900 ${
                                    compactValue
                                        ? "text-base sm:text-lg"
                                        : "text-xl sm:text-2xl"
                                }`}
                                title={String(value)}
                            >
                                {value}
                            </p>
                        </div>
                    </div>
                </article>
            ))}
        </section>
    );
};

export default ProductStatsCards;
